// assistant-markdown.tsx — React renderer over `@repo/chat-markdown`'s parsed tree, shared by the
// public playground (apps/web) and the dashboard's playground + transcripts (moved here 2026-08-22
// so the in-app playground stops showing raw `**`). Assistant replies
// are LLM-generated and therefore UNTRUSTED content, same as `apps/widget/src/markdown.ts`'s DOM
// renderer — this is the React-side twin of that file, built over the SAME shared parser so the
// two surfaces can never independently drift on what counts as safe markup or a safe link scheme.
//
// Every node here is a real React element (or a plain string, which React always renders as an
// escaped text node — never HTML-interpreted). `dangerouslySetInnerHTML` is never used in this
// file; that is the React-side equivalent of the widget's `createElement`/`createTextNode`-only
// rule, and is what makes untrusted assistant text safe to render at all. See `@repo/chat-
// markdown`'s `parse.ts` module comment for the full supported Markdown subset and the XSS
// rationale for the http/https-only link-scheme gate.
import { Fragment } from 'react';
import { parseMarkdown, type InlineLeaf, type InlineNode } from '@repo/chat-markdown';

function renderLeaf(leaf: InlineLeaf, key: string): React.ReactNode {
  return leaf.type === 'break' ? <br key={key} /> : <Fragment key={key}>{leaf.value}</Fragment>;
}

function renderInline(nodes: InlineNode[], keyPrefix: string): React.ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case 'text':
      case 'break':
        return renderLeaf(node, key);
      case 'strong':
        return <strong key={key}>{node.children.map((leaf, i) => renderLeaf(leaf, `${key}-${i}`))}</strong>;
      case 'link':
        return (
          <a key={key} href={node.href} target="_blank" rel="noopener noreferrer">
            {node.children.map((leaf, i) => renderLeaf(leaf, `${key}-${i}`))}
          </a>
        );
      default:
        return null;
    }
  });
}

/** Renders a constrained Markdown subset (bold, http/https links, `- ` bullet lists,
 * single/double newlines) as real React elements — never `dangerouslySetInnerHTML`. Mirrors
 * `apps/widget/src/markdown.ts`'s `renderMarkdown` exactly (same parser, same tree), so the
 * playground's assistant bubbles render identically to the shipped widget's. */
export function AssistantMarkdown({ text }: { text: string }): React.ReactElement {
  const blocks = parseMarkdown(text);
  return (
    <>
      {blocks.map((block, index) => {
        const key = `b-${index}`;
        if (block.type === 'list') {
          return (
            <ul key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }
        return <p key={key}>{renderInline(block.children, key)}</p>;
      })}
    </>
  );
}
